import React from 'react';
import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  IconButton,
  Button,
  useColorModeValue,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';
import {
  EditIcon,
  DeleteIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';
import { HiMiniUsers } from 'react-icons/hi2';

const DevelopmentTable = ({
  tableData,
  handleEditUser,
  handleDeleteUser,
  handlePageChange,
  handleRowClick,
  page,
  totalPages,
  loading,
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('gray.100', 'gray.700');
  const role = localStorage.getItem('role');
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const inactiveRowBg = useColorModeValue('red.50', 'red.900');
  const inactiveTextColor = useColorModeValue('inherit', 'white');
  const renderTableRows = () => {
    if (loading) {
      return Array.from({ length: 5 }, (_, index) => (
        <Tr key={index}>
          {Array.from({ length: 7 }, (_, colIndex) => (
            <Td key={colIndex}>
              <Skeleton height="20px" />
            </Td>
          ))}
        </Tr>
      ));
    }

    if (!tableData || tableData.length === 0) {
      return (
        <Tr>
          <Td colSpan="7" textAlign="center">
            <Text fontSize="lg" fontWeight="medium" color="gray.500">
              No users found
            </Text>
          </Td>
        </Tr>
      );
    }

    return tableData.map((user, index) => (
      <Tr
        key={user.id}
        onClick={() => handleRowClick(user)}
        cursor="pointer"
        _hover={{ bg: rowHoverBg, transform: 'scale(1.01)' }}
        transition="transform 0.2s"
        bg={user.active === false ? inactiveRowBg : 'transparent'}
        color={user.active === false ? inactiveTextColor : textColor}
      >
        <Td>{index + 1 + (page - 1) * 10}</Td>
        {/* <Td isTruncated>{user.name.toUpperCase()}</Td> */}
        <Td isTruncated title={user.name}>
          {user.name.split(' ').slice(0, 3).join(' ').toUpperCase()}
          {user.name.split(' ').length > 3 ? '...' : ''}
        </Td>

        <Td isTruncated>{user.role.toUpperCase()}</Td>
        <Td>{user?.division?.name.toUpperCase()}</Td>
        <Td>{user?.district?.name.toUpperCase()}</Td>
        <Td>{user?.tahsil?.name.toUpperCase()}</Td>
        {/* <Td isTruncated>
          <Tooltip label={user?.village?.map((v) => v.name).join(', ')}>
            <span>
              {Array.isArray(user?.village)
                ? user.village
                  .map((v) => v.name)
                  .join(', ')
                  .toUpperCase()
                : user?.village?.name?.toUpperCase() || 'N/A'}
            </span>
          </Tooltip>
        </Td>{' '} */}

        <Td maxW="250px" isTruncated>
          <Tooltip
            label={
              Array.isArray(user?.village)
                ? user.village.map((v) => v.name).join(', ')
                : user?.village?.name || 'N/A'
            }
            placement="top"
            hasArrow
          >
            <Box whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
              {Array.isArray(user?.village)
                ? user.village
                    .slice(0, 3)
                    .map((v) => v.name.toUpperCase())
                    .join(', ')
                : user?.village?.name?.toUpperCase() || 'N/A'}
            </Box>
          </Tooltip>
        </Td>
        <Td>{user.phoneNo.toUpperCase()}</Td>
        <Td py={2} px={3}>
          <Flex align="center" justify="center" h="100%">
            <IconButton
              aria-label="Edit"
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              onClick={(e) => {
                e.stopPropagation();
                handleEditUser(user);
              }}
            />
          </Flex>
          {/* {role !== 'companyUser' && role !== 'user' && (
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteUser(user._id);
                }}
              />
            )} */}
        </Td>
      </Tr>
    ));
  };

  const renderPagination = () => {
    const maxPagesToShow = 5;
    const pages = [];
    const isEllipsisShown = totalPages > maxPagesToShow;

    const createPageButton = (pageNumber) => (
      <Button
        key={pageNumber}
        onClick={() => handlePageChange(pageNumber)}
        colorScheme={page === pageNumber ? 'blue' : 'gray'}
        variant={page === pageNumber ? 'solid' : 'outline'}
        mx="1"
      >
        {pageNumber}
      </Button>
    );

    if (isEllipsisShown) {
      if (page > Math.ceil(maxPagesToShow / 2)) {
        pages.push(createPageButton(1));
        pages.push(
          <Text key="ellipsis-start" mx="1" fontSize="lg">
            ...
          </Text>,
        );
      }

      const startPage = Math.max(page - Math.floor(maxPagesToShow / 2), 1);
      const endPage = Math.min(
        page + Math.floor(maxPagesToShow / 2),
        totalPages,
      );
      for (let i = startPage; i <= endPage; i++) {
        pages.push(createPageButton(i));
      }

      if (page < totalPages - Math.floor(maxPagesToShow / 2)) {
        pages.push(
          <Text key="ellipsis-end" mx="1" fontSize="lg">
            ...
          </Text>,
        );
        pages.push(createPageButton(totalPages));
      }
    } else {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(createPageButton(i));
      }
    }

    return (
      <Flex mt="4" justify="center" align="center">
        <IconButton
          aria-label="Previous Page"
          icon={<ChevronLeftIcon />}
          isDisabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
          mr="2"
        />
        {pages}
        <IconButton
          aria-label="Next Page"
          icon={<ChevronRightIcon />}
          isDisabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
          ml="2"
        />
      </Flex>
    );
  };

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX="auto"
      pt="0"
      mt="0"
    >
      <Box overflowX="auto" maxHeight="calc(100vh - 100px)">
        <Table colorScheme="gray" mb="24px" size="sm">
          <Thead
            bgGradient="linear(to-r, blue.400, green.400)"
            position="sticky"
            top={0}
            zIndex={1}
            margin={0}
            padding={0}
          >
            <Tr>
              <Th borderColor={borderColor} color="white">
                {' '}
                Sr. No.
              </Th>
              <Th borderColor={borderColor} color="white">
                Name
              </Th>
              <Th borderColor={borderColor} color="white">
                Role
              </Th>
              <Th borderColor={borderColor} color="white">
                Division
              </Th>
              <Th borderColor={borderColor} color="white">
                District
              </Th>
              <Th borderColor={borderColor} color="white">
                Tahsil
              </Th>
              <Th borderColor={borderColor} color="white">
                Village
              </Th>
              <Th borderColor={borderColor} color="white">
                Phone No.
              </Th>
              <Th borderColor={borderColor} color="white">
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>{renderTableRows()}</Tbody>
        </Table>
      </Box>
      {renderPagination()}
    </Card>
  );
};

DevelopmentTable.propTypes = {
  tableData: PropTypes.array.isRequired,
  handleEditUser: PropTypes.func.isRequired,
  handleDeleteUser: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default React.memo(DevelopmentTable);
